/* eslint-disable max-len */
import { ValidationError } from 'yup';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import yup from './CustomValidation/yup-extended';

const SCREENER_START_DATE_LABEL = 'Screener Start Date';
const SCREENER_END_DATE_LABEL = 'Screener End Date';
const INTEGER_COUNT_REGEX = /^-?\d+$/;

const startOfDay = (value: Date): Date => {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
};

const isValidCalendarDateParts = (
  month: number,
  day: number,
  year: number
): boolean => {
  if (year < 1000 || year > 2999) {
    return false;
  }
  if (month < 1 || month > 12) {
    return false;
  }
  const daysInMonth = new Date(year, month, 0).getDate();
  return day >= 1 && day <= daysInMonth;
};

const toDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfDay(value);
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const datePart = raw.split('T')[0];
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    if (!isValidCalendarDateParts(month, day, year)) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  const typedMatch = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(raw);
  if (typedMatch) {
    const month = Number(typedMatch[1]);
    const day = Number(typedMatch[2]);
    const year = Number(typedMatch[3]);
    if (!isValidCalendarDateParts(month, day, year)) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  const compactMatch = /^(\d{2})(\d{2})(\d{4})$/.exec(raw);
  if (compactMatch) {
    const month = Number(compactMatch[1]);
    const day = Number(compactMatch[2]);
    const year = Number(compactMatch[3]);
    if (!isValidCalendarDateParts(month, day, year)) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  return null;
};

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  if (value instanceof Date) {
    return false;
  }

  return String(value).trim() === '';
};

const isValidDateFormat = (value: unknown): boolean => {
  if (isEmptyValue(value)) {
    return true;
  }

  return toDate(value) !== null;
};

const getSchoolYearDateBounds = (
  schoolYear: string | number
): { min: Date; max: Date } | null => {
  const endYear = Number(schoolYear);
  if (!Number.isFinite(endYear) || endYear <= 0) {
    return null;
  }

  return {
    min: new Date(endYear - 1, 6, 1),
    max: new Date(endYear, 5, 30),
  };
};

const isInSchoolYearAndNotAfterToday = (
  value: Date,
  schoolYear: string | number
): boolean => {
  const bounds = getSchoolYearDateBounds(schoolYear);
  const today = startOfDay(new Date());

  if (!bounds) {
    return value <= today;
  }

  const maxAllowed = today < bounds.max ? today : bounds.max;
  return value >= bounds.min && value <= maxAllowed;
};

const isIntegerCountOrEmpty = (value: unknown): boolean => {
  if (isEmptyValue(value)) {
    return true;
  }

  return INTEGER_COUNT_REGEX.test(String(value).trim());
};

const isNonNegativeCountOrEmpty = (value: unknown): boolean => {
  if (isEmptyValue(value) || !isIntegerCountOrEmpty(value)) {
    return true;
  }

  return Number(String(value).trim()) >= 0;
};

const createStudentCountFieldSchema = (isRequired = false): yup.AnySchema => {
  let schema = yup.mixed();

  if (isRequired) {
    schema = schema.test(
      'student-count-required',
      VALIDATION_MESSAGE.REQUIRED,
      (value) => !isEmptyValue(value)
    );
  }

  return schema
    .test(
      'student-count-must-be-whole-number',
      VALIDATION_MESSAGE.MUST_BE_NUMBER,
      isIntegerCountOrEmpty
    )
    .test(
      'student-count-non-negative',
      VALIDATION_MESSAGE.MUST_BE_GREATER_THAN_OR_EQUAL_TO_ZERO,
      isNonNegativeCountOrEmpty
    );
};

export interface LiteracyScreeningBoyValidationValues {
  screener?: unknown;
  screenerStartDate?: unknown;
  screenerEndDate?: unknown;
  studentsAssessed?: unknown;
  studentsBelowGradeLevel?: unknown;
  studentIntervention?: unknown;
}

export const getLiteracyScreeningBoyRowValidationSchema = (
  schoolYear: string | number
): yup.AnyObjectSchema =>
  yup.object().shape({
    screener: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    screenerStartDate: yup
      .mixed()
      .test(
        'screener-start-date-required',
        VALIDATION_MESSAGE.REQUIRED,
        (value) => !isEmptyValue(value)
      )
      .test(
        'screener-start-date-format',
        VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
        isValidDateFormat
      )
      .test(
        'screener-start-date-in-school-year',
        VALIDATION_MESSAGE.IN_SCHOOL_YEAR_AND_NOT_AFTER_TODAY(
          SCREENER_START_DATE_LABEL
        ),
        (value) => {
          if (isEmptyValue(value) || !isValidDateFormat(value)) {
            return true;
          }

          const startDate = toDate(value);
          if (!startDate) {
            return false;
          }

          return isInSchoolYearAndNotAfterToday(startDate, schoolYear);
        }
      )
      .test(
        'screener-start-date-not-after-end',
        VALIDATION_MESSAGE.MUST_BE_LESS_THAN_OR_EQUAL_TO_FIELD(
          SCREENER_START_DATE_LABEL,
          SCREENER_END_DATE_LABEL
        ),
        function validateStartAgainstEnd(value) {
          const startDate = toDate(value);
          const endDate = toDate(this.parent.screenerEndDate);
          if (!startDate || !endDate) {
            return true;
          }

          return startDate <= endDate;
        }
      ),
    screenerEndDate: yup
      .mixed()
      .test(
        'screener-end-date-required',
        VALIDATION_MESSAGE.REQUIRED,
        (value) => !isEmptyValue(value)
      )
      .test(
        'screener-end-date-format',
        VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
        isValidDateFormat
      )
      .test(
        'screener-end-date-in-school-year',
        VALIDATION_MESSAGE.IN_SCHOOL_YEAR_AND_NOT_AFTER_TODAY(
          SCREENER_END_DATE_LABEL
        ),
        (value) => {
          if (isEmptyValue(value) || !isValidDateFormat(value)) {
            return true;
          }

          const endDate = toDate(value);
          if (!endDate) {
            return false;
          }

          return isInSchoolYearAndNotAfterToday(endDate, schoolYear);
        }
      )
      .test(
        'screener-end-date-not-before-start',
        VALIDATION_MESSAGE.MUST_BE_GREATER_THAN_OR_EQUAL_TO_FIELD(
          SCREENER_END_DATE_LABEL,
          SCREENER_START_DATE_LABEL
        ),
        function validateEndAgainstStart(value) {
          const endDate = toDate(value);
          const startDate = toDate(this.parent.screenerStartDate);
          if (!endDate || !startDate) {
            return true;
          }

          return endDate >= startDate;
        }
      ),
    studentsAssessed: createStudentCountFieldSchema(true),
    studentsBelowGradeLevel: createStudentCountFieldSchema(true),
    studentIntervention: createStudentCountFieldSchema(true),
  });

export const getLiteracyScreeningBoyFieldError = (
  field: keyof LiteracyScreeningBoyValidationValues,
  values: LiteracyScreeningBoyValidationValues,
  schoolYear: string | number
): string | null => {
  try {
    getLiteracyScreeningBoyRowValidationSchema(schoolYear).validateSyncAt(
      field,
      values
    );
    return null;
  } catch (error) {
    if (error instanceof ValidationError) {
      return error.message;
    }

    return null;
  }
};

export const validateLiteracyScreeningBoyRow = (
  values: LiteracyScreeningBoyValidationValues,
  schoolYear: string | number
): string | null => {
  try {
    getLiteracyScreeningBoyRowValidationSchema(schoolYear).validateSync(values);
    return null;
  } catch (error) {
    if (error instanceof ValidationError) {
      return error.message;
    }

    return null;
  }
};

export const isLiteracyScreeningSessionGridComplete = (
  rows:
    | Array<LiteracyScreeningBoyValidationValues & { isTotal?: boolean }>
    | undefined,
  schoolYear: string | number
): boolean => {
  const dataRows = (rows || []).filter((row) => !row.isTotal);
  if (dataRows.length === 0) {
    return false;
  }

  return dataRows.every(
    (row) => validateLiteracyScreeningBoyRow(row, schoolYear) === null
  );
};

export const hasIncompleteEditedLiteracyScreeningSession = (
  sessions: Array<{ hasCommittedEdits: boolean; isComplete: boolean }>
): boolean =>
  sessions.some((session) => session.hasCommittedEdits && !session.isComplete);

export const getLiteracyScreeningDatePickerBounds = (
  schoolYear: string | number
): { minDate?: Date; maxDate: Date } => {
  const today = startOfDay(new Date());
  const bounds = getSchoolYearDateBounds(schoolYear);
  if (!bounds) {
    return { maxDate: today };
  }

  return {
    minDate: bounds.min,
    maxDate: today < bounds.max ? today : bounds.max,
  };
};

export interface LiteracyScreeningNotificationDatesValidationOptions {
  isBeginningOfYearGridComplete: boolean;
  isMiddleOfYearGridComplete: boolean;
  isEndOfYearGridComplete: boolean;
}

export interface LiteracyScreeningNotificationDatesValues {
  beginningOfYearNotificationDate?: unknown;
  middleOfYearNotificationDate?: unknown;
  endOfYearNotificationDate?: unknown;
}

const createNotificationDateSchema = (
  isRequired: boolean,
  schoolYear: string | number
): yup.AnySchema => {
  let schema = yup.mixed();

  if (isRequired) {
    schema = schema.test(
      'notification-date-required',
      VALIDATION_MESSAGE.REQUIRED,
      (value) => !isEmptyValue(value)
    );
  }

  return schema
    .test(
      'notification-date-format',
      VALIDATION_MESSAGE.LITERACY_SCREENING_NOTIFICATION_DATE,
      isValidDateFormat
    )
    .test(
      'notification-date-in-school-year',
      VALIDATION_MESSAGE.LITERACY_SCREENING_NOTIFICATION_DATE,
      (value) => {
        if (isEmptyValue(value) || !isValidDateFormat(value)) {
          return true;
        }

        const date = toDate(value);
        if (!date) {
          return false;
        }

        return isInSchoolYearAndNotAfterToday(date, schoolYear);
      }
    );
};

export const getLiteracyScreeningNotificationDatesValidationSchema = (
  schoolYear: string | number,
  options: LiteracyScreeningNotificationDatesValidationOptions
): yup.AnyObjectSchema =>
  yup.object().shape({
    beginningOfYearNotificationDate: createNotificationDateSchema(
      options.isBeginningOfYearGridComplete,
      schoolYear
    ),
    middleOfYearNotificationDate: createNotificationDateSchema(
      options.isMiddleOfYearGridComplete,
      schoolYear
    ),
    endOfYearNotificationDate: createNotificationDateSchema(
      options.isEndOfYearGridComplete,
      schoolYear
    ),
  });

export const isLiteracyScreeningNotificationDateComplete = (
  value: unknown,
  schoolYear: string | number
): boolean => {
  try {
    createNotificationDateSchema(true, schoolYear).validateSync(value);
    return true;
  } catch {
    return false;
  }
};

export const isLiteracyScreeningMoySessionEnabled = (
  isBoyGridComplete: boolean,
  isBoyNotificationComplete: boolean
): boolean => isBoyGridComplete && isBoyNotificationComplete;

export const isLiteracyScreeningEoySessionEnabled = (
  isBoyGridComplete: boolean,
  isMoyGridComplete: boolean,
  isBoyNotificationComplete: boolean,
  isMoyNotificationComplete: boolean
): boolean =>
  isBoyGridComplete &&
  isMoyGridComplete &&
  isBoyNotificationComplete &&
  isMoyNotificationComplete;
